import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles,
  Target,
  Eye,
  ShieldCheck,
  CheckCircle2,
  Heart,
  Building2,
  Users,
  Quote,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import HeroTemplateCandidats from '../../components/sections/HeroTemplateCandidats';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// — Typography aligned on home / campagnes / recrutement / decouvrir —
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass = 'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass =
  'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — 4 valeurs fondatrices ——————————————————————————————————————————
const values = [
  {
    icon: Sparkles,
    title: 'Innovation au service de l’humain',
    description:
      "La technologie n'est pas un gadget, mais un levier pour faire mieux, plus vite, et plus justement. IA appliquée au sourcing, no-code, automation, Plateforme Parcours Découverte propriétaire : nous intégrons les outils les plus récents pour enrichir chaque étape du processus RH — pas pour remplacer l'humain, pour l'amplifier.",
  },
  {
    icon: Target,
    title: 'Exigence, moteur de la qualité',
    description:
      "Être exigeant, c'est d'abord être exigeant avec soi-même. Rigueur méthodologique à chaque étape : sourcing, sélection, évaluation, accompagnement. Chaque mission est suivie de près, documentée, mesurée. Nous sélectionnons nos candidats avec soin — mais aussi nos clients et partenaires.",
  },
  {
    icon: Eye,
    title: 'Transparence, fondement de la confiance',
    description:
      "Relations honnêtes, tarifs clairs sans frais cachés, engagements précis. Nous informons clairement nos clients des étapes du processus, des profils présentés, des enjeux identifiés. Nous donnons aux candidats des feedbacks constructifs, sincères, sans faux-semblant — y compris en cas de refus.",
  },
  {
    icon: ShieldCheck,
    title: 'Intégrité et respect de l’autre',
    description:
      "Aucune compromission sur les sujets éthiques. Recrutement respectueux des personnes, inclusif, équitable. Aucune décision sur la base du genre, de l'origine, de l'âge, de la religion, de l'orientation sexuelle ou d'un quelconque critère discriminatoire. La parole donnée a du poids. La loyauté n'est pas négociable.",
  },
];

// — Engagements clients —————————————————————————————————————————————
const clientCommitments = [
  'Un interlocuteur dédié de bout en bout, capable d’anticiper, d’alerter, de conseiller',
  'Des prestations sur-mesure, adaptées à vos enjeux opérationnels — pas un catalogue figé',
  "Un suivi documenté de chaque mission : décisions, étapes, profils, jalons",
  "Une posture de partenaire stratégique, alignée sur vos objectifs business",
  'Une transparence totale sur les tarifs et les modalités, dès le premier appel',
];

// — Engagements candidats ———————————————————————————————————————————
const candidateCommitments = [
  'Une réponse écrite à 100 % des candidatures sous 48 h ouvrées — aucun ghosting',
  'Une information claire sur chaque étape du process, du premier contact à la signature',
  "Un retour structuré, même en cas de refus — pour que le candidat avance, peu importe l'issue",
  'Des outils de préparation : entraînement IA aux entretiens, accès au Parcours Découverte',
  "Le respect strict du RGPD : données utilisées uniquement dans le cadre de la mission, jamais revendues",
];

export default function ChartePage() {
  const navigate = useNavigate();

  return (
    <Layout
      title="Charte d'engagement RH · transparence et intégrité | recruteurs.io"
      description="4 valeurs opposables : innovation, exigence, transparence, intégrité. Tarifs clairs, feedback honnête, garantie écrite. Le contrat moral de recruteurs.io vis-à-vis clients et candidats."
      keywords="charte recrutement, engagement cabinet rh, transparence recrutement, éthique recrutement, intégrité rh, valeurs cabinet recrutement, recruteurs.io"
    >
      <WebPageSchema
        page={{
          title:
            'Notre charte d’engagement | recruteurs.io',
          description:
            "La charte d'engagement de recruteurs.io : 4 valeurs fondatrices (innovation, exigence, transparence, intégrité) et engagements opposables vis-à-vis de nos clients et candidats.",
          url: 'https://recruteurs.io/infos/charte',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Infos', url: 'https://recruteurs.io/infos/charte' },
          { name: 'Charte', url: 'https://recruteurs.io/infos/charte' },
        ]}
      />

      {/* HERO — pastel cohérent avec les pages engagement candidat */}
      <HeroTemplateCandidats
        page="charte"
        title="Notre charte d’engagement."
        eyebrow="Recruteurs.io · Cassis · Marseille · Aix · PACA"
        description="Une charte n'est pas un slogan. C'est un contrat moral, opposable, vérifiable. Voici ce que recruteurs.io s'engage à faire — vis-à-vis de ses clients, de ses candidats, et de ses partenaires."
        imageUrl="https://ygkvhunsrhvgddngoulm.supabase.co/storage/v1/object/public/backgrounds/backgrounds/pourquoi-recruteurs.io.webp"
        breadcrumbs={[
          { name: 'Infos', href: '/infos/charte' },
          { name: 'Charte', href: '/infos/charte' },
        ]}
        cta1={{
          label: 'Réserver un échange',
          onClick: () => navigate('/reservation'),
        }}
        cta2={{
          label: 'Voir le Parcours Découverte',
          onClick: () => navigate('/decouvrir'),
        }}
      />

      {/* SECTION 2 — Préambule (qui, pourquoi cette charte) */}
      <SectionBackground page="charte" section="charte-intro" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-10">
            <span className={eyebrowClass}>Pourquoi cette charte</span>

            <h2 className={h2Class}>
              Une conviction simple :{' '}
              <span className={gradientText}>
                allier l’exigence opérationnelle à l’innovation, en respectant l’humain.
              </span>
            </h2>
          </div>

          <div className="rounded-[2rem] bg-[#fbfaf8] p-8 md:p-10 ring-1 ring-neutral-200 max-w-4xl mx-auto space-y-5">
            <p className={bodyClass}>
              recruteurs.io est une agence de conseil RH fondée par <strong>Gérald
              Gaillard</strong>, basée à Cassis (11 chemin du Mont Gibaou, 13260),
              qui intervient sur Marseille, Aix-en-Provence et toute la PACA.
            </p>
            <p className={bodyClass}>
              Fort d’une expérience confirmée en direction des ressources
              humaines — en scale-up comme en contexte dégradé — Gérald a
              construit cette agence avec une conviction simple&nbsp;: allier
              l’exigence opérationnelle à l’innovation technologique, tout en
              respectant profondément l’humain.
            </p>
            <p className={bodyClass}>
              Cette charte structure nos engagements vis-à-vis de nos
              <strong> clients</strong>, de nos <strong>partenaires</strong>, de
              nos <strong>candidats</strong> et de notre <strong>équipe</strong>.
              Elle n’est pas un texte marketing&nbsp;: c’est un contrat moral
              opposable.
            </p>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — 4 valeurs fondatrices */}
      <SectionBackground page="charte" section="charte-values" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Nos quatre valeurs fondatrices</span>

            <h2 className={h2Class}>
              Quatre piliers.{' '}
              <span className={gradientText}>Une seule colonne vertébrale.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Chacune de ces valeurs guide nos décisions, nos outils, notre
              posture face aux enjeux quotidiens.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="rounded-[1.75rem] bg-white p-8 ring-1 ring-neutral-200 shadow-sm hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-5 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                      <Icon className="w-7 h-7" />
                    </div>
                    <span className="text-xs font-semibold text-neutral-300 uppercase tracking-widest">
                      0{index + 1}
                    </span>
                  </div>
                  <h3 className={h3Class}>{value.title}</h3>
                  <p className={bodyClass}>{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Engagements clients + candidats (2 colonnes) */}
      <SectionBackground
        page="charte"
        section="charte-commitments"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Nos engagements concrets</span>

            <h2 className={h2Class}>
              Vis-à-vis de nos clients.{' '}
              <span className={gradientText}>Vis-à-vis de nos candidats.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Pas des promesses générales. Des engagements précis,
              vérifiables. Et opposables si nous y manquons.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Carte CLIENTS */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5 }}
              className="rounded-[2rem] bg-[#fbfaf8] p-8 md:p-10 ring-1 ring-neutral-200"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                  <Building2 className="w-6 h-6" />
                </div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                  Envers nos clients
                </p>
              </div>
              <h3 className="text-2xl font-bold text-[#30475e] leading-tight mb-3">
                Plus qu’un prestataire,{' '}
                <span className={gradientText}>un partenaire stratégique.</span>
              </h3>
              <p className={`${bodyClass} mb-6`}>
                Nous croyons qu’un cabinet RH doit aligner son exigence sur les
                objectifs business de votre organisation. Cinq engagements
                concrets&nbsp;:
              </p>
              <ul className="space-y-3">
                {clientCommitments.map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-neutral-200"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#d03484] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-800 leading-snug">
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Carte CANDIDATS */}
            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, delay: 0.08 }}
              className="rounded-[2rem] bg-[#fbfaf8] p-8 md:p-10 ring-1 ring-neutral-200"
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white shadow-lg shadow-[#d03484]/20">
                  <Users className="w-6 h-6" />
                </div>
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest">
                  Envers nos candidats
                </p>
              </div>
              <h3 className="text-2xl font-bold text-[#30475e] leading-tight mb-3">
                Chaque candidature{' '}
                <span className={gradientText}>est un engagement mutuel.</span>
              </h3>
              <p className={`${bodyClass} mb-6`}>
                Postuler chez nous, c’est nous accorder du temps. Le minimum
                qu’on doit en retour, c’est du respect, de la clarté, de
                l’accompagnement. Cinq engagements concrets&nbsp;:
              </p>
              <ul className="space-y-3">
                {candidateCommitments.map((line) => (
                  <li
                    key={line}
                    className="flex items-start gap-3 rounded-2xl bg-white px-4 py-3 ring-1 ring-neutral-200"
                  >
                    <CheckCircle2 className="w-5 h-5 text-[#d03484] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-neutral-800 leading-snug">
                      {line}
                    </span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Donner du temps (citation) */}
      <SectionBackground page="charte" section="charte-time" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-3xl mx-auto">
            <div className="rounded-[2rem] bg-white p-10 md:p-14 ring-1 ring-neutral-200 shadow-sm relative overflow-hidden">
              <div className="absolute top-6 right-6 opacity-10">
                <Quote className="w-24 h-24 text-[#d03484]" />
              </div>
              <div className="relative z-10">
                <p className="text-xs font-semibold text-neutral-400 uppercase tracking-widest mb-4">
                  Notre conviction de fond
                </p>
                <h3 className="text-3xl md:text-4xl font-bold text-[#30475e] leading-tight mb-6">
                  Donner du temps,{' '}
                  <span className={gradientText}>pour en recevoir.</span>
                </h3>
                <p className={`${bodyClass} text-lg`}>
                  Nous prenons le temps d’<strong>écouter</strong> les besoins
                  de nos clients. De <strong>comprendre</strong> les projets de
                  nos candidats. D’<strong>expliquer</strong> nos méthodes. Ce
                  temps investi est le socle d’un service de qualité, d’une
                  mission réussie, d’une relation durable.
                </p>
              </div>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — Manifeste / synthèse */}
      <SectionBackground
        page="charte"
        section="charte-manifesto"
        className="bg-white"
      >
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-12">
            <span className={eyebrowClass}>En résumé</span>

            <h2 className={h2Class}>
              Pas un simple texte.{' '}
              <span className={gradientText}>Une ligne directrice.</span>
            </h2>
          </div>

          <div className="rounded-[2rem] bg-gradient-to-br from-[#d03484] to-[#d47254] p-10 md:p-14 max-w-4xl mx-auto shadow-2xl shadow-[#d03484]/30">
            <div className="text-center text-white">
              <Heart className="w-12 h-12 mx-auto mb-6 fill-white/20" />
              <h3 className="text-2xl md:text-3xl font-bold leading-tight mb-6">
                Notre charte n’est pas un texte marketing.<br />
                C’est un contrat moral. Un manifeste.
              </h3>
              <p className="text-base md:text-lg leading-relaxed text-white/90 max-w-2xl mx-auto">
                Chaque ligne reflète notre ambition&nbsp;:{' '}
                <strong>professionnaliser la fonction RH</strong>,{' '}
                <strong>moderniser le recrutement</strong>, et{' '}
                <strong>replacer l’humain au centre des décisions</strong>.
              </p>
              <p className="mt-6 text-sm text-white/80 italic">
                Nous sommes recruteurs.io. Et nous prenons cette mission très
                au sérieux.
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 7 — CTA */}
      <SectionBackground page="charte" section="charte-cta" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Prêt à parler{' '}
              <span className={gradientText}>de votre prochain recrutement&nbsp;?</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Cette charte vous parle&nbsp;? C’est qu’on partage un certain
              regard sur le métier. Le meilleur moyen de continuer la
              conversation, c’est un échange direct, sans engagement.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Réserver un échange (15 min)
              </button>
              <button
                onClick={() => navigate('/decouvrir')}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                Voir le Parcours Découverte
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
